// The sales pipeline's pure half (F-HQ-39, spec/14 §16): reading a phone number, filling a message template, building the
// WhatsApp link, the stage rules and the calendar day the daily outreach cap counts in. No I/O: the same answer in the
// server and in the console's preview.
import { LEAD_LOST_REASONS, LEAD_STAGES, OPEN_LEAD_STAGES } from '@clavian/contracts';
import { shiftLocalDate, startOfVenueDay, venueLocalMoment } from "../time/index.js";
// The vocabulary lives in the contract (the wire enums are built from it); re-exported so callers of the rules need one import.
export { LEAD_LOST_REASONS, LEAD_STAGES, OPEN_LEAD_STAGES };
export function isOpenLeadStage(stage) {
    return OPEN_LEAD_STAGES.includes(stage);
}
/**
 * May a lead move from `from` to `to`? An open stage goes to any other stage; a won, lost or parked lead can be reopened to
 * an open stage; staying where it is, and one closed stage to another, are refused. (A reason for `lost` is the service's.)
 */
export function canMoveLeadStage(from, to) {
    if (from === to)
        return false;
    if (isOpenLeadStage(from))
        return true;
    return isOpenLeadStage(to);
}
/** Read an Indian phone number as written ("098765 43210", "+91 98765-43210", "(020) 4729 4519"), or `null`. */
export function normalizeIndianPhone(input) {
    const trimmed = input.trim();
    if (!/^[+\d\s\-().]+$/.test(trimmed))
        return null;
    let digits = trimmed.replace(/\D/g, '');
    if (trimmed.startsWith('+')) {
        if (!digits.startsWith('91'))
            return null;
        digits = digits.slice(2);
    }
    else if (digits.length === 12 && digits.startsWith('91')) {
        digits = digits.slice(2);
    }
    else if (digits.length === 11 && digits.startsWith('0')) {
        digits = digits.slice(1);
    }
    if (!/^\d{10}$/.test(digits) || digits.startsWith('0') || digits.startsWith('1'))
        return null;
    return { e164: `+91${digits}`, mobile: /^[6-9]/.test(digits) };
}
export const LEAD_TEMPLATE_PLACEHOLDERS = ['business', 'contact', 'city', 'area', 'hook', 'sender'];
/** Fill `{{business}}`-style placeholders. A missing value renders empty and is listed; an unknown placeholder is left as written and listed. */
export function renderLeadTemplate(body, vars) {
    const missing = [];
    const note = (name) => {
        if (!missing.includes(name))
            missing.push(name);
    };
    let emptied = false;
    const filled = body.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (whole, name) => {
        if (!LEAD_TEMPLATE_PLACEHOLDERS.includes(name)) {
            note(name);
            return whole;
        }
        const value = vars[name]?.trim() ?? '';
        if (value === '') {
            note(name);
            emptied = true;
            return '';
        }
        return value;
    });
    // Tidy only where a value went missing, so a message written with deliberate spacing is left alone.
    const text = emptied ? filled.replace(/[ \t]{2,}/g, ' ').replace(/ +([,.!?;:])/g, '$1').replace(/[ \t]+\n/g, '\n').trim() : filled;
    return { text, missing };
}
/**
 * The WhatsApp chat link for an E.164 number, with `text` prefilled. Built on `api.whatsapp.com/send` rather than `wa.me`: the
 * `wa.me` short link answers with a redirect to `api.whatsapp.com/send/` that mangles the text (every emoji arrives as U+FFFD,
 * verified with `curl -sI`), while `api.whatsapp.com` keeps the percent-encoded UTF-8 intact.
 */
export function whatsAppUrl(e164, text) {
    return `https://api.whatsapp.com/send?phone=${e164.replace(/\D/g, '')}&text=${encodeURIComponent(text)}`;
}
/** The calendar day of `now` in `timeZone` as the half-open instants `[start, end)`, in epoch milliseconds. */
export function leadDayWindow(now, timeZone) {
    const tz = timeZone;
    const millis = typeof now === 'number' ? now : now.getTime();
    const { date } = venueLocalMoment(millis, tz);
    return { start: startOfVenueDay(date, tz), end: startOfVenueDay(shiftLocalDate(date, 1), tz) };
}
//# sourceMappingURL=index.js.map